import dayjs from 'dayjs';
import _ from 'lodash';
import 'dayjs/locale/fr';
import { Button, Header, Segment } from '@mairistem/composants';
import { Liste, ListeTable, ListeTableActionCsv, ListeTableActionPrint } from '@mairistem/liste';
import React, { type ReactNode } from 'react';
import { ModalConfirm } from '@/components/Autre';
import { ARRETES } from '@/constants';
import { useCreateArreteAnnuel } from '@/hooks';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import { getArretes, searchArretes } from '@/routes';
import type { ArreteType } from '@/types';
import { getAnneeArreteAnnuelCreable } from '@/utils';
import { useRegieStore } from '@/zustand';
import ArreteAnnuelListeTableHeader from './ArreteAnnuelListeTableHeader';
import ArreteAnnuelListeTableRow from './ArreteAnnuelListeTableRow';

dayjs.locale('fr');

const ArreteAnnuel = () => {
  const regie = useRegieStore((state) => state.regie);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const { createArreteAnnuel, creating } = useCreateArreteAnnuel();

  const fixedParams = React.useMemo(
    () => ({
      type: ARRETES.TYPE_ANNUEL,
      perPage: 500,
    }),
    [],
  );

  const { items, loading, search, setSorts, setPage, setSearch, reset } = useListeSearchSort({
    active: !_.isNil(regie?.identifiant) && !creating,
    getItems: getArretes,
    searchItems: searchArretes,
    fixedParams,
    all: true,
  });

  // Années déjà couvertes par un arrêté annuel — c'est ce qui conditionne le bouton de création.
  // On ne le met à jour QUE sur la liste complète (recherche vide) : une recherche filtre `items`
  // côté serveur, et ne doit donc pas modifier l'état du bouton. `null` = liste pas encore chargée.
  const [anneesExistantes, setAnneesExistantes] = React.useState<number[] | null>(null);

  React.useEffect(() => {
    if (search === '' && items !== undefined) {
      setAnneesExistantes(
        items.map((arrete) => dayjs((arrete as ArreteType).dateDebutPeriode).year()),
      );
    }
  }, [items, search]);

  // Le bouton apparaît dès que le dernier arrêté de la régie est un Périodique finissant à la date
  // d'ouverture de la fenêtre ou après : l'année à clôturer est celle de cette date de fin (pas la
  // date du jour), donc la création reste possible y compris après bascule d'exercice. L'année déjà
  // couverte par un arrêté est déduite de `dateDebutPeriode` (même logique que les arrêtés
  // périodiques), pas du libellé. Dès qu'un arrêté annuel existe pour l'année cible, le bouton est
  // masqué (pas seulement désactivé).
  const { boutonVisible, canCreate, targetAnnee } = React.useMemo(() => {
    // Année cible dérivée du dernier périodique de la régie (selon la fenêtre configurée/légale).
    const annee = getAnneeArreteAnnuelCreable(
      regie?.typeDernierArrete,
      regie?.dateFinDernierArrete,
      regie?.arreteConfig,
    );

    // Pas d'année créable (dernier arrêté pas Périodique, ou fin avant le seuil) → bouton masqué.
    if (annee === null) {
      return { boutonVisible: false, canCreate: false, targetAnnee: dayjs().year() };
    }

    // Tant que la liste complète n'est pas chargée, on ne peut pas statuer sur l'unicité : on ne
    // masque PAS (évite un flash), on désactive seulement.
    if (anneesExistantes === null) {
      return { boutonVisible: true, canCreate: false, targetAnnee: annee };
    }

    // Règle uniforme : un arrêté existe déjà pour l'année cible → on MASQUE le bouton.
    if (anneesExistantes.includes(annee)) {
      return { boutonVisible: false, canCreate: false, targetAnnee: annee };
    }

    return { boutonVisible: true, canCreate: true, targetAnnee: annee };
  }, [
    anneesExistantes,
    regie?.typeDernierArrete,
    regie?.dateFinDernierArrete,
    regie?.arreteConfig,
  ]);

  // `loading` n'entre PAS dans la condition : une recherche fait osciller `loading` sans devoir
  // modifier l'état du bouton, qui est figé sur la liste complète via `anneesExistantes`.
  const buttonDisabled = !canCreate || creating || _.isNil(regie?.identifiant);

  const handleSort = React.useCallback(
    (sort) => {
      setSorts(
        _.isEmpty(sort) ? null : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
    },
    [setSorts],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const handleSearch = React.useCallback(
    (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const handleConfirmCreate = React.useCallback(async () => {
    setConfirmOpen(false);
    const ok = await createArreteAnnuel(targetAnnee);
    if (ok) {
      // La liste affichée peut être filtrée par une recherche : on marque tout de suite l'année
      // comme existante pour désactiver le bouton, sans dépendre du filtre courant.
      setAnneesExistantes((prev) => (prev ? [...prev, targetAnnee] : [targetAnnee]));
      reset();
    }
  }, [createArreteAnnuel, targetAnnee, reset]);

  const renderRow = (item: ArreteType, _invalidate: unknown, rowProps: Record<string, unknown>) => (
    <ArreteAnnuelListeTableRow item={item} rowProps={rowProps} />
  );

  const createButton = (
    <span>
      <Button
        positive
        size="large"
        content={`Arrêter la régie pour l'année ${targetAnnee}`}
        disabled={buttonDisabled}
        loading={creating}
        onClick={() => setConfirmOpen(true)}
      />
    </span>
  );

  return (
    <>
      <Segment
        inverted
        secondary
        style={{
          flex: 1,
          overflow: 'auto',
          background: 'transparent',
        }}
      >
        <Segment style={{ borderRadius: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Header as="h1" style={{ marginBottom: '1em' }} textAlign="center">
              <Header.Content>Arrêtés annuels</Header.Content>
            </Header>
            <Liste
              title=""
              // @ts-expect-error onSearch est bien supporté
              onSearch={handleSearch}
              loading={loading}
              searchable
              search={{
                value: search,
                placeholder: 'Rechercher un arrêté annuel...',
                clearable: true,
                autoFocus: true,
              }}
            >
              <Liste.Header>{''}</Liste.Header>
              <Liste.Content>
                <div style={{ height: 450 }}>
                  <ListeTable
                    count={_.isEmpty(items) ? 0 : items.length}
                    loading={loading}
                    items={items as ArreteType[]}
                    onSort={handleSort}
                    onLoad={handleLoad}
                    perPage={500}
                    noResultsIcon=""
                    noResultsMessage="Aucun arrêté annuel trouvé pour cette régie"
                  >
                    <ListeTable.Header>
                      <ArreteAnnuelListeTableHeader />
                    </ListeTable.Header>
                    {renderRow as unknown as ReactNode}
                    <ListeTable.Actions>
                      <ListeTableActionCsv
                        items={(items || []) as ArreteType[]}
                        columns={['libelle', 'date', 'montantTotal']}
                        options={{ filename: 'Arrêtés annuels' }}
                      />
                      <ListeTableActionPrint
                        printOptions={{
                          documentName: 'Arrêtés annuels',
                        }}
                      />
                    </ListeTable.Actions>
                  </ListeTable>
                </div>
                {boutonVisible && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: '1em',
                      paddingRight: '1em',
                    }}
                  >
                    {createButton}
                  </div>
                )}
              </Liste.Content>
            </Liste>
          </div>
        </Segment>
      </Segment>
      {confirmOpen && (
        <ModalConfirm
          header="Créer l'arrêté annuel"
          content={`Confirmez-vous la création de l'arrêté annuel ${targetAnnee} ? Cette action est définitive.`}
          confirmText="Créer"
          cancelText="Annuler"
          loading={creating}
          onConfirm={handleConfirmCreate}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
};

export default ArreteAnnuel;
